import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, AtomBadge, StatusPill, PriorityPill, EmptyState } from "../components/primitives";
import { NetworkGraph, graphForSubject } from "../components/NetworkGraph";
import { formatNGN } from "../lib/format";

const TABS = ["Summary","Network","Timeline","Evidence","Reasoning","Notes","Actions"];

export default function CaseDetail() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Summary");

  useEffect(() => { api.get(`/cases/${cid}`).then(r => setData(r.data)); }, [cid]);
  if (!data) return <div className="p-6 text-[13px] text-ink-muted">Loading case…</div>;
  const { case: c, narrative, typology } = data;
  const graph = graphForSubject(c.subject, c.subject_cid, c.typology, c.amount);

  return (
    <div>
      <PageHeader
        title={`${c.id} · ${c.subject}`}
        subtitle={`${typology.name} · Opened ${c.opened}`}
        sections={c.cbn}
        atom={<AtomBadge model="Atom Nucleus" version="v3.5" />}
      />
      <div className="px-6 border-b border-line bg-bg-surface">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t} data-testid={`case-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`px-3 py-2 text-[12px] border-b-2 -mb-px ${tab===t?'border-amber text-ink font-medium':'border-transparent text-ink-muted hover:text-ink'}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {tab === "Summary" && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="col-span-1">
              <CardHeader title="Case summary" />
              <div className="p-3 text-[12px] space-y-1.5">
                <div className="flex justify-between"><span className="text-ink-subtle">Priority</span><PriorityPill priority={c.priority} /></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Status</span><StatusPill status={c.status} /></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Owner</span><span>{c.owner}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">MLRO</span><span>{c.mlro}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Originating alert</span><Link to={`/alerts/${c.alert}`} className="text-amber-600">{c.alert}</Link></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Subject</span><Link to={`/customers/${c.subject_cid}`} className="text-amber-600">{c.subject}</Link></div>
                <div className="flex justify-between"><span className="text-ink-subtle">AMLTRIX</span><span>{typology.amltrix}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Tactic</span><span>{typology.tactic}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">CBN</span><span>{c.cbn.join(", ")}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">FATF</span><span>{(typology.fatf||[]).join(", ")}</span></div>
                <div className="flex justify-between pt-2 border-t border-line"><span className="text-ink-subtle">Amount at risk</span><span className="font-medium tabular">{formatNGN(c.amount)}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Maker</span><span>{c.owner}</span></div>
                <div className="flex justify-between"><span className="text-ink-subtle">Checker</span>{c.pending_checker ? <span className="text-risk-medium">Pending {c.checker}</span> : c.checker ? <span>{c.checker}</span> : <span className="text-ink-subtle">—</span>}</div>
              </div>
            </Card>
            <Card className="col-span-2">
              <CardHeader title="Atom Nucleus reasoning" subtitle="Audit-grade explanation" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
              <div className="p-5 border-l-2 border-amber/40 bg-amber/4 mx-5 my-4 text-[13px] leading-relaxed whitespace-pre-wrap">{narrative}</div>
              <div className="px-5 pb-5 text-[11px] text-ink-subtle">
                Recommended next action: <span className="font-medium text-ink">Maker-checker approval to file STR-2026 with NFIU.</span>
                <div className="mt-2 flex items-center gap-2">
                  <button className="px-2.5 py-1 text-[11px] bg-risk-low/15 text-risk-low border border-risk-low/40 rounded-sm">Agree</button>
                  <button className="px-2.5 py-1 text-[11px] border border-line rounded-sm">Disagree (reason required)</button>
                  <span className="text-[10px] text-ink-subtle">Feedback flows to Atom Photon v1.0</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {tab === "Network" && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="col-span-2">
              <CardHeader title="Counterparty network" subtitle="Force-directed graph of relationships uncovered by Atom Nucleus" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
              <div className="p-3">
                <NetworkGraph subject={graph.subject} nodes={graph.nodes} edges={graph.edges} width={720} height={440} />
              </div>
            </Card>
            <div className="space-y-3">
              <Card>
                <CardHeader title="Network stats" />
                <div className="p-3 text-[12px] space-y-1.5">
                  <div className="flex justify-between"><span className="text-ink-subtle">Nodes</span><span className="tabular font-medium">{graph.nodes.length + 1}</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">Edges</span><span className="tabular font-medium">{graph.edges.length}</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">Hops to sanctions</span><span className="tabular font-medium">2</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">PEP corridors</span><span className="tabular font-medium">1</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">Mule indicators</span><span className="tabular font-medium">1</span></div>
                  <div className="flex justify-between"><span className="text-ink-subtle">FX channels</span><span className="tabular font-medium">1</span></div>
                  <div className="flex justify-between pt-2 border-t border-line"><span className="text-ink-subtle">Risk-weighted exposure</span><span className="tabular font-medium">{formatNGN(c.amount, {short:true})}</span></div>
                </div>
              </Card>
              <Card>
                <CardHeader title="Network actions" />
                <div className="p-3 space-y-2">
                  <button className="w-full text-left px-3 py-2 text-[12px] bg-navy text-white rounded-sm hover:bg-navy-700">Expand 2-hop neighbours</button>
                  <button className="w-full text-left px-3 py-2 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Recompute network</button>
                  <button className="w-full text-left px-3 py-2 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Export as PDF</button>
                  <button className="w-full text-left px-3 py-2 text-[12px] border border-amber/40 bg-amber/8 text-amber-600 rounded-sm hover:bg-amber/15">Run on Atom Nucleus →</button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {tab === "Timeline" && (
          <Card>
            <CardHeader title="Case timeline" subtitle="Immutable event log — chronological" />
            <div className="p-3">
              {[
                ["22 May 2026 09:18 WAT","System (Atom Proton v2.4)","Alert raised · " + c.alert],
                ["22 May 2026 09:42 WAT","Noah Kessler","Case opened · assigned to self"],
                ["22 May 2026 10:08 WAT","Noah Kessler","Customer 360 reviewed; 90-day baseline pulled"],
                ["22 May 2026 10:34 WAT","Noah Kessler","Network analysis run via Atom Nucleus v3.5"],
                ["22 May 2026 11:18 WAT","Noah Kessler","STR draft initiated"],
                ["22 May 2026 11:42 WAT","Noah Kessler","Submitted for MLRO review"],
                ["22 May 2026 12:08 WAT","Ethan Brennan (MLRO)","Reviewed; awaiting maker-checker sign-off"],
                ["22 May 2026 14:33 WAT","System","Status set to " + c.status],
              ].map(([t,u,a],i) => (
                <div key={i} className="flex gap-3 py-2 border-b border-line/50 last:border-0">
                  <div className="text-[11px] text-ink-subtle min-w-[160px]">{t}</div>
                  <div className="text-[12px] min-w-[180px] font-medium">{u}</div>
                  <div className="text-[12px] flex-1">{a}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {tab === "Evidence" && (
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardHeader title="Implicated transactions" />
              <div className="p-3 text-[12px]">All transactions matching the typology pattern have been pulled into this case. Pattern alignment: {typology.amltrix}.</div>
              <table className="dense w-full"><thead><tr><th>Reference</th><th>Date</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {Array.from({length:6}).map((_,i) => <tr key={i}><td className="hash-mono text-[11px]">TXN2026051{4-i}…</td><td className="text-[11px]">{14-i} May 2026 WAT</td><td className="text-right">{formatNGN(c.amount/6, {short:true})}</td></tr>)}
                </tbody>
              </table>
            </Card>
            <Card>
              <CardHeader title="Supporting evidence" />
              <div className="p-3 space-y-2 text-[12px]">
                <div className="border border-line rounded-sm p-2">Customer profile · pulled from Customer 360</div>
                <div className="border border-line rounded-sm p-2">Screening result · OFAC/UN/EU/CBN/PEP · pulled at case open</div>
                <div className="border border-line rounded-sm p-2">Network analysis output · Atom Nucleus v3.5</div>
                <div className="border border-line rounded-sm p-2">90-day customer baseline · Atom Proton v2.4</div>
                <button className="px-3 py-1.5 text-[12px] border border-line rounded-sm hover:bg-bg-hover mt-2">Upload supporting document</button>
              </div>
            </Card>
          </div>
        )}

        {tab === "Reasoning" && (
          <Card>
            <CardHeader title="Atom Nucleus audit-grade explanation" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
            <div className="p-5 text-[13px] leading-relaxed whitespace-pre-wrap">{narrative}</div>
            <div className="px-5 pb-5 text-[11px] text-ink-subtle border-t border-line pt-3">
              Citations: CBN Sections {c.cbn.join(", ")} · AMLTRIX {typology.amltrix} ({typology.name}) · FATF {(typology.fatf||[]).join(", ")} · Atom Photon explainability score 0.92.
            </div>
          </Card>
        )}

        {tab === "Notes" && (
          <Card>
            <CardHeader title="Notes & collaboration" subtitle="Internal-only — visible to bank staff" />
            <div className="p-3 space-y-2">
              <div className="border border-line rounded-sm p-3">
                <div className="text-[11px] text-ink-subtle">22 May 2026 10:42 WAT · Noah Kessler</div>
                <div className="text-[12px] mt-1">Initial review complete. The pattern is consistent across multiple branches. Recommending escalation to Ethan @MLRO for STR consideration. cc @Marcus</div>
              </div>
              <div className="border border-line rounded-sm p-3">
                <div className="text-[11px] text-ink-subtle">22 May 2026 12:08 WAT · Ethan Brennan</div>
                <div className="text-[12px] mt-1">Confirmed STR drafting. Will route to maker-checker once Atom Nucleus narrative ratified.</div>
              </div>
              <textarea placeholder="Add note… (use @ to mention)" className="w-full border border-line rounded-sm p-2 text-[12px]" rows={3} />
              <button className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-sm">Add note</button>
            </div>
          </Card>
        )}

        {tab === "Actions" && (
          <Card>
            <CardHeader title="Case actions" subtitle="Maker-checker enforcement on consequential actions" />
            <div className="p-4 grid grid-cols-2 gap-2">
              {[
                ["Assign / Reassign", "navy"],["Add transaction","outline"],["Add note","outline"],["Request info from RM","outline"],
                ["Escalate to MLRO","navy"],["Recommend SAR","amber"],["Draft STR","amber"],
                ["Close — cleared (checker required)","outline-risk"],["Close — other action (checker required)","outline-risk"],["Reopen","outline"],
              ].map(([l, k]) => {
                const cls = k === "navy" ? "bg-navy text-white" : k === "amber" ? "bg-amber/15 border border-amber/40 text-amber-600" : k === "outline-risk" ? "border border-risk-high/40 text-risk-high" : "border border-line";
                return <button key={l} className={`px-3 py-2 text-[12px] rounded-sm text-left hover:opacity-90 ${cls}`}>{l}</button>
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
